import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';
import { mockLibrarySettings } from '@/data/adminMockData';

const SettingsModule: React.FC = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState(mockLibrarySettings);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const updateGeneralSetting = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        [key]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const updateHours = (day: string, field: string, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: {
          ...prev.hours[day],
          [field]: value
        }
      }
    }));
    setHasUnsavedChanges(true);
  };

  const updatePolicy = (category: string, role: string, value: number) => {
    setSettings(prev => ({
      ...prev,
      policies: {
        ...prev.policies,
        [category]: {
          ...prev.policies[category],
          [role]: value
        }
      }
    }));
    setHasUnsavedChanges(true);
  };

  const updateFinePolicy = (field: string, value: number) => {
    setSettings(prev => ({
      ...prev,
      policies: {
        ...prev.policies,
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveSettings = () => {
    toast({
      title: 'Settings Saved',
      description: 'All settings have been saved successfully'
    });
    setHasUnsavedChanges(false);
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const roles = ['Student', 'Faculty', 'Staff', 'Librarian'];

  return (
    <div className="max-w-[1400px]">
      <div className="mb-6">
        <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Settings</h1>
        <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Configure library policies and preferences.</p>
      </div>

      {/* General Information */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">General Information</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Basic library information and contact details.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Library Name</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Displayed on all public pages.</div>
            </div>
            <input
              value={settings.general.name}
              onChange={e => updateGeneralSetting('name', e.target.value)}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Timezone</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Used for scheduling and time display.</div>
            </div>
            <select
              value={settings.general.timezone}
              onChange={e => updateGeneralSetting('timezone', e.target.value)}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            >
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
          <div className="flex items-start justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Address</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Physical location of the library.</div>
            </div>
            <textarea
              value={settings.general.address}
              onChange={e => updateGeneralSetting('address', e.target.value)}
              rows={3}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100 resize-none"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Phone Number</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Contact number for the library.</div>
            </div>
            <input
              value={settings.general.phone}
              onChange={e => updateGeneralSetting('phone', e.target.value)}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Email Address</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Official library contact email.</div>
            </div>
            <input
              type="email"
              value={settings.general.email}
              onChange={e => updateGeneralSetting('email', e.target.value)}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Website</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Library website URL.</div>
            </div>
            <input
              value={settings.general.website}
              onChange={e => updateGeneralSetting('website', e.target.value)}
              className="w-56 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Operating Hours */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">Operating Hours</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Opening and closing times for each day of the week.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          {days.map(day => (
            <div key={day} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <span className="text-[13px] text-[var(--lib-ink)] capitalize w-24">{day}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!settings.hours[day].closed}
                    onCheckedChange={checked => updateHours(day, 'closed', !checked)}
                    className="data-[state=checked]:bg-[var(--lib-gold)]"
                  />
                  <span className="font-mono text-[10px] text-[var(--lib-ink-muted)]">
                    {settings.hours[day].closed ? 'Closed' : 'Open'}
                  </span>
                </div>
              </div>
              {!settings.hours[day].closed && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[var(--lib-ink-muted)]">Open</span>
                    <input
                      type="time"
                      value={settings.hours[day].open}
                      onChange={e => updateHours(day, 'open', e.target.value)}
                      className="px-2 py-1 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[var(--lib-ink-muted)]">Close</span>
                    <input
                      type="time"
                      value={settings.hours[day].close}
                      onChange={e => updateHours(day, 'close', e.target.value)}
                      className="px-2 py-1 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Loan Policies */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">Loan Policies</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Borrowing limits and loan periods per user role.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          {/* Max Books */}
          <div className="px-6 py-5">
            <div className="text-[13px] text-[var(--lib-ink)] mb-3">Maximum Books Per User</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {roles.map(role => (
                <div key={role}>
                  <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mb-1">{role}</div>
                  <input
                    type="number"
                    min="1"
                    value={settings.policies.maxBooksPerUser[role]}
                    onChange={e => updatePolicy('maxBooksPerUser', role, parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Loan Period */}
          <div className="px-6 py-5">
            <div className="text-[13px] text-[var(--lib-ink)] mb-3">Loan Period (Days)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {roles.map(role => (
                <div key={role}>
                  <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mb-1">{role}</div>
                  <input
                    type="number"
                    min="1"
                    value={settings.policies.loanPeriodDays[role]}
                    onChange={e => updatePolicy('loanPeriodDays', role, parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Max Renewals */}
          <div className="px-6 py-5">
            <div className="text-[13px] text-[var(--lib-ink)] mb-3">Maximum Renewals</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {roles.map(role => (
                <div key={role}>
                  <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mb-1">{role}</div>
                  <input
                    type="number"
                    min="0"
                    value={settings.policies.maxRenewals[role]}
                    onChange={e => updatePolicy('maxRenewals', role, parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Fine Policies */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">Fine Policies</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Configure overdue fines and penalty caps.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Fine Per Day ($)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Amount charged per day for overdue books.</div>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.policies.finePerDay}
              onChange={e => updateFinePolicy('finePerDay', parseFloat(e.target.value))}
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Maximum Fine Amount ($)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Maximum fine that can be charged per book.</div>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.policies.maxFineAmount}
              onChange={e => updateFinePolicy('maxFineAmount', parseFloat(e.target.value))}
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* System Settings */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">System Settings</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Advanced configuration for notifications and automation.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Email Notifications</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Send automated email reminders to users.</div>
            </div>
            <Switch id="email-notifications" defaultChecked className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Auto-renewals</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Automatically renew books if no holds exist.</div>
            </div>
            <Switch id="auto-renewals" className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Late Fee Calculations</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Automatically calculate and track overdue fines.</div>
            </div>
            <Switch id="late-fees" defaultChecked className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Hold Notifications</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Notify users when requested books become available.</div>
            </div>
            <Switch id="hold-notifications" defaultChecked className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">Security Settings</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">User authentication and access control.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Session Timeout (minutes)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Time before users are automatically logged out.</div>
            </div>
            <input
              type="number"
              min="5"
              defaultValue="30"
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Password Expiry (days)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Days before users must change their password.</div>
            </div>
            <input
              type="number"
              min="30"
              defaultValue="90"
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Two-Factor Authentication</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Require 2FA for admin accounts.</div>
            </div>
            <Switch id="two-factor" className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Password Complexity</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Enforce strong password requirements.</div>
            </div>
            <Switch id="password-complexity" defaultChecked className="data-[state=checked]:bg-[var(--lib-gold)]" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg mb-5">
        <div className="px-6 py-5 border-b border-[var(--lib-hairline)]">
          <h2 className="text-base font-semibold text-[var(--lib-ink)]">Data Management</h2>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Backup frequency and data retention configuration.</p>
        </div>
        <div className="divide-y divide-[var(--lib-hairline)]">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Backup Frequency</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">How often automatic backups are taken.</div>
            </div>
            <select
              defaultValue="daily"
              className="w-40 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Backup Retention (days)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">How long backup files are kept.</div>
            </div>
            <input
              type="number"
              min="1"
              defaultValue="30"
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Log Retention (days)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">How long system logs are stored.</div>
            </div>
            <input
              type="number"
              min="1"
              defaultValue="90"
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <div className="text-[13px] text-[var(--lib-ink)]">Transaction History (years)</div>
              <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">How long loan transaction records are kept.</div>
            </div>
            <input
              type="number"
              min="1"
              defaultValue="7"
              className="w-32 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] bg-transparent text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center gap-3 px-6 py-4">
            <button className="px-4 py-1.5 text-[13px] border border-[var(--lib-hairline)] text-[var(--lib-ink)] rounded-md hover:bg-[var(--lib-surface-2)] transition-colors font-medium">
              Backup Now
            </button>
            <button className="px-4 py-1.5 text-[13px] border border-[var(--lib-hairline)] text-[var(--lib-ink)] rounded-md hover:bg-[var(--lib-surface-2)] transition-colors font-medium">
              Restore Data
            </button>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--lib-hairline)] flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save changes
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-[var(--lib-danger)]/5 border border-[var(--lib-danger)] rounded-lg mb-5 p-6">
        <h2 className="text-base font-semibold text-[var(--lib-danger)] mb-4">Danger Zone</h2>
        <div className="flex items-center justify-between py-3 border-b border-[var(--lib-danger)]/30">
          <div>
            <div className="text-[13px] text-[var(--lib-ink)]">Clear All Fines</div>
            <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Waive all outstanding fines across all users. This action cannot be undone.</div>
          </div>
          <button className="px-3 py-1.5 text-sm border border-[var(--lib-danger)] text-[var(--lib-danger)] rounded-lg hover:bg-[var(--lib-danger)]/10 transition-colors">
            Clear Fines
          </button>
        </div>
        <div className="flex items-center justify-between py-3 border-b border-[var(--lib-danger)]/30">
          <div>
            <div className="text-[13px] text-[var(--lib-ink)]">Reset Loan Counters</div>
            <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Reset all active loan and renewal counters. This action cannot be undone.</div>
          </div>
          <button className="px-3 py-1.5 text-sm border border-[var(--lib-danger)] text-[var(--lib-danger)] rounded-lg hover:bg-[var(--lib-danger)]/10 transition-colors">
            Reset Counters
          </button>
        </div>
        <div className="flex items-center justify-between py-3">
          <div>
            <div className="text-[13px] text-[var(--lib-ink)]">Purge Audit Logs</div>
            <div className="font-mono text-[10px] text-[var(--lib-ink-muted)] mt-0.5">Permanently delete all audit log entries. This action cannot be undone.</div>
          </div>
          <button className="px-3 py-1.5 text-sm border border-[var(--lib-danger)] text-[var(--lib-danger)] rounded-lg hover:bg-[var(--lib-danger)]/10 transition-colors">
            Purge Logs
          </button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[var(--lib-ink)] text-[var(--lib-surface)] text-sm rounded-lg px-5 py-3 flex items-center gap-4 shadow-lg z-50">
          <span>You have unsaved changes.</span>
          <button
            onClick={handleSaveSettings}
            className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium rounded-md px-3 py-1.5 transition-colors flex items-center gap-1.5 text-sm"
          >
            <Save className="h-3.5 w-3.5" /> Save now
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsModule;
